import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { Guid, Nillable, once } from '@web/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AdminBannerDetailsFormComponent } from '../admin-banner-details-form/admin-banner-details-form.component';
import { AdminBannerDetailsForm } from '../../models/admin-banner-details-form';
import { AdminBannerDataSourceService } from '../../services/admin-banner-data-source.service';
import { BannerLocationType } from '../../../shared/enums/banner-location-type';

@Component({
  selector: 'app-admin-banner-edit',
  imports: [
    AdminBannerDetailsFormComponent,
    AlertsComponent,
    CommonModule,
    SpinnerFilledComponent,
  ],
  templateUrl: './admin-banner-edit.component.html',
  styleUrl: './admin-banner-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminBannerEditComponent {
  private readonly bannerDataSource = inject(AdminBannerDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly id = input.required<Guid>();
  public readonly location = input<Nillable<BannerLocationType>>(null);
  public readonly allowedLocations = input<BannerLocationType[]>([
    BannerLocationType.HomePageHeader,
    BannerLocationType.SubscribePageHeader,
  ]);
  public readonly submitSuccessEvent = output<void>();

  protected readonly id$ = toObservable(this.id);
  protected readonly form$ = this.formSource();

  public handleSubmitClickEvent(data: AdminBannerDetailsForm): void {
    const location = this.location();
    const request$ = location
      ? this.bannerDataSource.updateByFormDataAndLocation({
          id: this.id(),
          data,
          location,
        })
      : this.bannerDataSource.updateHeaderByFormData({
          id: this.id(),
          data,
        });

    request$.pipe(once()).subscribe({
      next: () => this.notifySubmitSuccessEvent(),
      error: (response) => this.showFailureAlertByResponse(response),
    });
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private formSource(): Observable<AdminBannerDetailsForm> {
    return this.id$.pipe(
      switchMap((id) =>
        this.location()
          ? this.bannerDataSource.fetchDetailsFormByIdAndLocation({
              id,
              location: this.location()!,
            })
          : this.bannerDataSource.fetchHeaderDetailsFormById({ id }),
      ),
    );
  }
}
